import React, { useEffect, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StatusPill } from "@/components/StatusPill";
import { QRCodeSVG } from "qrcode.react";
import { Plus, Share2, Copy, ArrowLeft, Trash2, MessageCircle, Package, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// ============ Collections list ============
export function CollectionsListPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => setItems((await api.get("/collections")).data);
  useEffect(() => { load(); }, []);

  const canCreate = ["admin", "staff"].includes(user?.role);

  return (
    <div className="space-y-6" data-testid="collections-list-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Catalog</div>
          <h1 className="font-serif text-3xl lg:text-4xl font-medium">Collections</h1>
          <p className="text-[#4A5A50] mt-1">Curated groups of products you can share as a beautiful public link.</p>
        </div>
        {canCreate && <Button onClick={() => setDialogOpen(true)} className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="new-collection-btn"><Plus size={14} className="mr-1"/> New Collection</Button>}
      </div>

      {items.length === 0 ? (
        <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="py-16 text-center">
          <Package size={32} className="text-[#C9A24B] mx-auto mb-3" strokeWidth={1.5}/>
          <div className="font-serif text-xl">No collections yet</div>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(c => (
            <button key={c.id} onClick={() => nav(`/collections/${c.id}`)} className="text-left bg-white rounded-xl border border-[#E4DFCF] overflow-hidden card-hover" data-testid={`collection-card-${c.id}`}>
              <div className="aspect-[16/9] bg-[#F1EDDF] overflow-hidden">
                {c.cover_image ? <img src={fileUrl(c.cover_image)} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center text-[#C9A24B]"><Package size={40} strokeWidth={1.2}/></div>}
              </div>
              <div className="p-4">
                <div className="font-serif text-xl">{c.name}</div>
                <div className="text-xs text-[#4A5A50] mt-0.5">{c.product_count} products</div>
                {c.description && <div className="text-sm text-[#4A5A50] mt-2 line-clamp-2">{c.description}</div>}
              </div>
            </button>
          ))}
        </div>
      )}

      <CollectionFormDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={() => { setDialogOpen(false); load(); }}/>
    </div>
  );
}

function CollectionFormDialog({ open, onOpenChange, onSaved, initial }) {
  const [f, setF] = useState({ name: "", description: "", cover_image: "", is_public: true, product_ids: [] });
  const [products, setProducts] = useState([]);
  useEffect(() => {
    if (open) {
      setF(initial || { name: "", description: "", cover_image: "", is_public: true, product_ids: [] });
      (async () => setProducts((await api.get("/products", { params: { page_size: 100 } })).data.items || []))();
    }
  }, [open, initial]);

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setF({ ...f, cover_image: data.url }); }
    catch { toast.error("Upload failed"); }
  };
  const save = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    try {
      if (initial?.id) await api.patch(`/collections/${initial.id}`, f);
      else await api.post("/collections", f);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="collection-form-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{initial ? "Edit" : "New"} Collection</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={f.name} onChange={(e)=>setF({...f, name:e.target.value})} className="mt-1" data-testid="coll-name-input"/></div>
          <div><Label>Description</Label><Textarea rows={2} value={f.description} onChange={(e)=>setF({...f, description:e.target.value})} className="mt-1"/></div>
          <div><Label>Cover image</Label>
            <div className="mt-1.5 flex items-center gap-3">
              {f.cover_image ? <img src={fileUrl(f.cover_image)} className="w-32 h-20 object-cover rounded-lg border border-[#E4DFCF]" alt=""/> : null}
              <label className="w-32 h-20 border-2 border-dashed border-[#E4DFCF] rounded-lg flex items-center justify-center cursor-pointer text-[#4A5A50]">
                <Upload size={14}/><input type="file" accept="image/*" className="hidden" onChange={(e)=>e.target.files?.[0] && upload(e.target.files[0])}/>
              </label>
            </div>
          </div>
          <div>
            <Label>Products in this collection</Label>
            <div className="mt-1.5 max-h-64 overflow-y-auto border border-[#E4DFCF] rounded-lg divide-y divide-[#E4DFCF]">
              {products.map(p => {
                const on = f.product_ids.includes(p.id);
                return (
                  <label key={p.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[#F1EDDF]">
                    <input type="checkbox" checked={on} onChange={() => setF({ ...f, product_ids: on ? f.product_ids.filter(x=>x!==p.id) : [...f.product_ids, p.id] })}/>
                    <div className="w-8 h-8 bg-[#F1EDDF] rounded overflow-hidden">{p.media?.[0] && <img src={fileUrl(p.media[0].thumb_url || p.media[0].url)} alt="" className="w-full h-full object-cover"/>}</div>
                    <div className="text-sm flex-1">{p.title} <span className="text-[#4A5A50] text-xs">· {p.sku}</span></div>
                  </label>
                );
              })}
              {products.length === 0 && <div className="text-sm text-[#4A5A50] px-3 py-4">Create products first</div>}
            </div>
          </div>
          <label className="flex items-center gap-2"><Switch checked={f.is_public} onCheckedChange={(v)=>setF({...f, is_public:v})}/> <span className="text-sm">Public (shareable)</span></label>
        </div>
        <DialogFooter><Button variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button><Button onClick={save} className="bg-[#0F5E39] hover:bg-[#083F22] text-white rounded-full" data-testid="save-collection-btn">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ Collection detail ============
export function CollectionDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, company } = useAuth();
  const [c, setC] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => { const { data } = await api.get(`/collections/${id}`); setC(data); };
  useEffect(() => { load(); }, [id]);

  if (!c) return <div className="text-center py-16 text-[#4A5A50]">Loading…</div>;

  const shareUrl = `${window.location.origin}/store/${company?.code}/collections/${c.id}`;
  const canEdit = ["admin", "staff"].includes(user?.role);
  const del = async () => { if (!window.confirm("Delete?")) return; await api.delete(`/collections/${id}`); nav("/collections"); };

  return (
    <div className="space-y-6" data-testid="collection-detail-page">
      <button onClick={() => nav(-1)} className="text-sm text-[#4A5A50] hover:text-[#0F1E15] inline-flex items-center gap-1"><ArrowLeft size={14}/> Back</button>

      {c.cover_image && <div className="aspect-[21/6] rounded-2xl overflow-hidden border border-[#E4DFCF]"><img src={fileUrl(c.cover_image)} alt="" className="w-full h-full object-cover"/></div>}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Collection</div>
          <h1 className="font-serif text-4xl font-medium mt-1">{c.name}</h1>
          {c.description && <p className="text-[#4A5A50] mt-1 max-w-2xl">{c.description}</p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShareOpen(true)} className="rounded-full" data-testid="collection-share-btn"><Share2 size={14} className="mr-1.5"/> Share</Button>
          <Button variant="outline" onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`${c.name}\n\n${c.description || ""}\n\nView: ${shareUrl}`)}`, "_blank")} className="rounded-full border-[#25D366] text-[#1B8B4B] hover:bg-[#25D366]/10" data-testid="collection-whatsapp-btn"><MessageCircle size={14} className="mr-1.5"/> WhatsApp</Button>
          {canEdit && <Button variant="outline" onClick={() => setEditOpen(true)} className="rounded-full">Edit</Button>}
          {user?.role === "admin" && <Button variant="outline" onClick={del} className="rounded-full text-red-600 border-red-200 hover:bg-red-50"><Trash2 size={14}/></Button>}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {c.products?.map(p => (
          <button key={p.id} onClick={() => nav(`/products/${p.id}`)} className="text-left bg-white rounded-xl border border-[#E4DFCF] overflow-hidden card-hover" data-testid={`coll-product-${p.id}`}>
            <div className="aspect-square bg-[#F1EDDF] overflow-hidden">
              {p.media?.[0] ? <img src={fileUrl(p.media[0].thumb_url || p.media[0].url)} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center text-[#C9A24B]"><Package size={28} strokeWidth={1.2}/></div>}
            </div>
            <div className="p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">{p.metal} · {p.purity}</div>
              <div className="font-serif text-base mt-0.5 line-clamp-1">{p.title}</div>
              {p.is_price_visible && <div className="text-sm text-[#C9A24B] font-medium mt-1">₹{(p.computed_price || 0).toLocaleString('en-IN')}</div>}
            </div>
          </button>
        ))}
        {c.products?.length === 0 && <div className="col-span-full text-sm text-[#4A5A50] py-8 text-center">No products yet. Edit the collection to add some.</div>}
      </div>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent data-testid="collection-share-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Share collection</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2 bg-[#F1EDDF] rounded-lg p-2 text-xs">
              <div className="flex-1 truncate">{shareUrl}</div>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Copied"); }} className="rounded-full"><Copy size={12}/></Button>
            </div>
            <div className="flex justify-center bg-white p-4 rounded-lg border border-[#E4DFCF]">
              <QRCodeSVG value={shareUrl} size={192} fgColor="#0F1E15" bgColor="#FFFFFF"/>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <CollectionFormDialog open={editOpen} onOpenChange={setEditOpen} initial={c} onSaved={() => { setEditOpen(false); load(); }}/>
    </div>
  );
}
